/**
 * V205 — Configuración → "Programa de lealtad" (§29, TyC 3.3 §7). Solo SUPER_USER y
 * verticales con POS. Secciones:
 *  1. Encender/apagar. GATE: exige el aviso de privacidad publicado con la finalidad
 *     "fidelidad" (el backend lo enforce con 409; aquí se explica a dónde ir). Mientras
 *     esté encendido, el aviso no se puede despublicar ni quitarle esa casilla.
 *  2. Caducidad de sellos PARCIALES (mín. 30 días, TyC §7.3; la recompensa ganada no expira).
 *  3. "Lo de siempre" (D3) — APAGADO por defecto; para encenderlo el tenant confirma que su
 *     aviso cubre "ofrecer productos según historial" (queda en legal_acceptances).
 *  4. Campañas (cartilla N visitas → producto del catálogo) + PDF con QR (los 4 campos).
 *  5. Miembros y reporte de cortesías por cajero.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api, apiDownload } from '../lib/api';
import { formatLoyaltyCode } from './loyaltyCode';

interface Settings {
  enabled: boolean; usualOrderEnabled: boolean; partialExpiryDays: number;
  avisoPublished: boolean; avisoCoversFidelidad: boolean; slug: string | null;
  usualNotice: { type: string; version: string; text: string; accepted: boolean };
}
interface Campaign {
  id: string; name: string; visitsRequired: number; rewardLabel: string; conditions: string;
  startsOn: string; endsOn: string; archivedAt: string | null; vigente: boolean;
}
interface Member {
  id: string; code: string; hasPhone: boolean; enrolledAt: string; visits: number;
  rewardsPending: number; rewardsRedeemed: number; lastVisitAt: string | null;
}
interface CourtesyReport {
  total: number;
  byCashier: Array<{ cashierUserId: string | null; cashier: string; count: number;
    items: Array<{ id: string; at: string; description: string; memberCode: string; branch: string | null }> }>;
}
interface Product { id: string; name: string; variants?: Array<{ id: string; name: string }> }

const errMsg = (e: any, fallback: string): string => e?.body?.message || e?.message || fallback;
const fmtDate = (ymd: string) => ymd.split('-').reverse().join('/');
const todayMx = () => new Date(Date.now() - 6 * 3600_000).toISOString().slice(0, 10);
const addDays = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
};
const btn = 'rounded-xl px-3 py-1.5 text-xs font-medium disabled:opacity-50';

export function LoyaltyAdminCard() {
  const [s, setS] = useState<Settings | null>(null);
  const [hidden, setHidden] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [confirmUsual, setConfirmUsual] = useState(false);
  const [usualAck, setUsualAck] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api('/loyalty/settings') as Settings;
      setS(r); setDays(r.partialExpiryDays);
    } catch { setHidden(true); }
  };
  useEffect(() => { load(); }, []);

  const enrollUrl = s?.slug ? `${window.location.origin}/fidelidad/${s.slug}` : null;
  useEffect(() => {
    if (enrollUrl && s?.enabled) QRCode.toDataURL(enrollUrl, { margin: 1, width: 200 }).then(setQr).catch(() => {});
  }, [enrollUrl, s?.enabled]);

  if (hidden) return null;
  if (!s) return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Cargando…</div>;

  const save = async (body: Record<string, unknown>, ok: string) => {
    setBusy(true); setMsg(null);
    try {
      const r = await api('/loyalty/settings', { method: 'PUT', body }) as Settings;
      setS(r); setDays(r.partialExpiryDays); setMsg({ kind: 'ok', text: ok });
      return true;
    } catch (e: any) {
      setMsg({ kind: 'err', text: errMsg(e, 'No se pudo guardar.') });
      return false;
    } finally { setBusy(false); }
  };

  const canEnable = s.avisoCoversFidelidad;

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Programa de lealtad</h3>
        <p className="text-sm text-slate-500">
          Sellos por visita: cada compra pagada en la que el cliente muestra su código suma un sello; al completar la
          cartilla recibe una cortesía (ticket de promoción $0, sin carga en el corte).
        </p>
      </div>

      <div className="space-y-5 px-6 py-5 md:px-8">
        {/* 1 · Interruptor + gate de aviso */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-800">{s.enabled ? 'Encendido' : 'Apagado'}</p>
            {!canEnable && !s.enabled && (
              <p className="text-xs text-amber-700">
                {s.avisoPublished
                  ? 'Tu aviso de privacidad publicado no declara la finalidad "Fidelidad". Republícalo marcándola (Usuarios, acceso y legal → Aviso de privacidad para tus clientes).'
                  : 'Publica tu aviso de privacidad declarando la finalidad "Fidelidad" para encender el programa (Usuarios, acceso y legal → Aviso de privacidad para tus clientes).'}
              </p>
            )}
            {s.enabled && <p className="text-xs text-slate-500">Mientras esté encendido, tu aviso no se puede despublicar ni quitarle la finalidad "Fidelidad".</p>}
          </div>
          <button type="button" disabled={busy || (!s.enabled && !canEnable)}
            onClick={() => save({ enabled: !s.enabled }, s.enabled ? 'Programa apagado.' : 'Programa encendido.')}
            className={`${btn} ${s.enabled ? 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
            {s.enabled ? 'Apagar' : 'Encender'}
          </button>
        </div>

        {s.enabled && enrollUrl && (
          <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 p-3">
            {qr && <img src={qr} alt="QR de alta" className="h-24 w-24 rounded-lg border border-slate-100 bg-white" />}
            <div className="min-w-0 flex-1 text-xs text-slate-600">
              <p className="font-medium text-slate-800">Liga de alta para tus clientes</p>
              <input readOnly value={enrollUrl} onFocus={(e) => e.target.select()}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs" />
              <p className="mt-1">El PDF de cada campaña ya trae este QR con las bases, vigencia, condiciones y quién ofrece. También puedes ponerlo en el QR configurable del ticket.</p>
            </div>
          </div>
        )}

        {/* 2 · Caducidad de parciales */}
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
            <span>Los sellos parciales caducan a los</span>
            <input type="number" min={30} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm" aria-label="Días de caducidad de sellos parciales" />
            <span>días desde la última visita</span>
          </label>
          <button type="button" disabled={busy || days === s.partialExpiryDays}
            onClick={() => save({ partialExpiryDays: days }, 'Caducidad guardada.')}
            className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50`}>Guardar</button>
          <p className="w-full text-xs text-slate-500">Mínimo 30 días (TyC §7.3). La recompensa ya ganada no expira. El PDF imprime este plazo.</p>
        </div>

        {/* 3 · Lo de siempre (D3) */}
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-slate-800">"Lo de siempre" en la caja</p>
              <p className="text-xs text-slate-500">Al identificar al miembro, la caja sugiere sus 3–5 productos más frecuentes. Apagado por defecto.</p>
            </div>
            {s.usualOrderEnabled ? (
              <button type="button" disabled={busy} onClick={() => save({ usualOrderEnabled: false }, '"Lo de siempre" apagado.')}
                className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50`}>Apagar</button>
            ) : (
              <button type="button" disabled={busy || !s.enabled}
                onClick={() => (s.usualNotice.accepted ? save({ usualOrderEnabled: true }, '"Lo de siempre" encendido.') : setConfirmUsual(true))}
                className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>Encender</button>
            )}
          </div>
          {confirmUsual && !s.usualOrderEnabled && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3 ring-1 ring-amber-200">
              <label className="flex items-start gap-2 text-xs text-amber-900">
                <input type="checkbox" className="mt-0.5" checked={usualAck} onChange={(e) => setUsualAck(e.target.checked)} />
                <span>{s.usualNotice.text}</span>
              </label>
              <div className="mt-2 flex gap-2">
                <button type="button" disabled={!usualAck || busy}
                  onClick={async () => {
                    if (await save({ usualOrderEnabled: true, acceptUsualNotice: true }, '"Lo de siempre" encendido.')) {
                      setConfirmUsual(false); setUsualAck(false);
                    }
                  }}
                  className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>Confirmar y encender</button>
                <button type="button" onClick={() => { setConfirmUsual(false); setUsualAck(false); }}
                  className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200`}>Cancelar</button>
              </div>
            </div>
          )}
        </div>

        {msg && (
          <p className={`rounded-xl px-3 py-2 text-sm ring-1 ${msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-rose-50 text-rose-700 ring-rose-200'}`}>
            {msg.text}
          </p>
        )}

        <Campaigns />
        <Members />
        <CourtesyReportView />
      </div>
    </div>
  );
}

function Campaigns() {
  const [list, setList] = useState<Campaign[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const t = todayMx();
  const [f, setF] = useState({ name: '', visitsRequired: 5, rewardProductId: '', rewardVariantId: '', conditions: '', startsOn: t, endsOn: addDays(t, 90) });

  const load = () => api('/loyalty/campaigns').then((r) => setList(r as Campaign[])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open && !products.length) api('/boutique/products').then((r) => setProducts(r as Product[])).catch(() => {});
  }, [open, products.length]);

  const variants = products.find((p) => p.id === f.rewardProductId)?.variants ?? [];

  const create = async () => {
    setErr(null);
    try {
      await api('/loyalty/campaigns', { method: 'POST', body: {
        ...f, rewardVariantId: f.rewardVariantId || undefined, visitsRequired: Number(f.visitsRequired),
      } });
      setOpen(false); setF({ ...f, name: '', conditions: '' }); load();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo crear la campaña.')); }
  };
  const archive = async (id: string) => {
    try { setList(await api(`/loyalty/campaigns/${id}/archive`, { method: 'POST' }) as Campaign[]); } catch (e: any) { setErr(errMsg(e, 'No se pudo archivar.')); }
  };
  const pdf = async (c: Campaign) => {
    setErr(null);
    try { await apiDownload(`/loyalty/campaigns/${c.id}/material.pdf`, `lealtad_${c.name.replace(/\W+/g, '_')}.pdf`); }
    catch (e: any) { setErr(errMsg(e, 'No se pudo generar el PDF.')); }
  };

  return (
    <section>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Campañas (cartillas)</p>
        <button type="button" onClick={() => setOpen((v) => !v)} className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50`}>
          {open ? 'Cerrar' : 'Nueva campaña'}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-2">
          <label className="text-xs text-slate-600 sm:col-span-2">Nombre
            <input className="input mt-1 w-full" value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Cliente frecuente" />
          </label>
          <label className="text-xs text-slate-600">Visitas para la recompensa
            <input type="number" min={2} max={50} className="input mt-1 w-full" value={f.visitsRequired} onChange={(e) => setF({ ...f, visitsRequired: Number(e.target.value) })} />
          </label>
          <label className="text-xs text-slate-600">Recompensa (de tu catálogo)
            <select className="input mt-1 w-full" value={f.rewardProductId} onChange={(e) => setF({ ...f, rewardProductId: e.target.value, rewardVariantId: '' })}>
              <option value="">Elige un producto…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {variants.length > 0 && (
            <label className="text-xs text-slate-600 sm:col-span-2">Variante
              <select className="input mt-1 w-full" value={f.rewardVariantId} onChange={(e) => setF({ ...f, rewardVariantId: e.target.value })}>
                <option value="">Elige la variante…</option>
                {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
          )}
          <label className="text-xs text-slate-600">Inicio de vigencia
            <input type="date" className="input mt-1 w-full" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} />
          </label>
          <label className="text-xs text-slate-600">Fin de vigencia
            <input type="date" className="input mt-1 w-full" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} />
          </label>
          <label className="text-xs text-slate-600 sm:col-span-2">Condiciones (van en el PDF; escríbelas tú)
            <textarea className="input mt-1 w-full" rows={3} maxLength={1500} value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })}
              placeholder="Ej.: Válido en compras pagadas en cualquier sucursal. No acumulable con otras promociones." />
          </label>
          <div className="sm:col-span-2">
            <button type="button" onClick={create}
              disabled={!f.name.trim() || !f.rewardProductId || !f.conditions.trim() || (variants.length > 0 && !f.rewardVariantId)}
              className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>Crear campaña</button>
          </div>
        </div>
      )}
      {err && <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">{err}</p>}
      <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
        {list.length === 0 && <li className="px-4 py-3 text-sm text-slate-500">Aún no hay campañas.</li>}
        {list.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">
                {c.name}{' '}
                {c.vigente && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Vigente</span>}
                {c.archivedAt && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">Archivada</span>}
              </p>
              <p className="text-xs text-slate-500">{c.visitsRequired} visitas → {c.rewardLabel} · {fmtDate(c.startsOn)} a {fmtDate(c.endsOn)}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => pdf(c)} className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50`}>PDF con QR</button>
              {!c.archivedAt && (
                <button type="button" onClick={() => archive(c.id)} className={`${btn} bg-white text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50`}>Archivar</button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Members() {
  const [rows, setRows] = useState<Member[] | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open && !rows) api('/loyalty/members').then((r) => setRows(r as Member[])).catch(() => setRows([])); }, [open, rows]);
  return (
    <section>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 hover:text-slate-600">
        Miembros {open ? '▾' : '▸'}
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="px-3 py-2">Código</th><th className="px-3 py-2">Teléfono</th><th className="px-3 py-2">Visitas</th>
              <th className="px-3 py-2">Por canjear</th><th className="px-3 py-2">Canjeadas</th><th className="px-3 py-2">Última visita</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(rows ?? []).map((m) => (
                <tr key={m.id}>
                  <td className="px-3 py-2 font-mono">{formatLoyaltyCode(m.code)}</td>
                  <td className="px-3 py-2">{m.hasPhone ? 'Sí' : '—'}</td>
                  <td className="px-3 py-2 tabular-nums">{m.visits}</td>
                  <td className="px-3 py-2 tabular-nums">{m.rewardsPending}</td>
                  <td className="px-3 py-2 tabular-nums">{m.rewardsRedeemed}</td>
                  <td className="px-3 py-2">{m.lastVisitAt ? new Date(m.lastVisitAt).toLocaleDateString('es-MX') : '—'}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-slate-500">Sin miembros todavía.</td></tr>}
            </tbody>
          </table>
          <p className="px-3 py-2 text-[11px] text-slate-400">El teléfono nunca se guarda en claro (solo un hash con la llave de tu negocio).</p>
        </div>
      )}
    </section>
  );
}

function CourtesyReportView() {
  const t = todayMx();
  const [from, setFrom] = useState(addDays(t, -30));
  const [to, setTo] = useState(t);
  const [r, setR] = useState<CourtesyReport | null>(null);
  const [open, setOpen] = useState(false);
  const load = () => api(`/loyalty/reports/courtesies?from=${from}&to=${to}`).then((x) => setR(x as CourtesyReport)).catch(() => setR(null));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);
  return (
    <section>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 hover:text-slate-600">
        Cortesías por cajero {open ? '▾' : '▸'}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-end gap-2 text-xs">
            <label>Desde <input type="date" className="input ml-1" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label>Hasta <input type="date" className="input ml-1" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <button type="button" onClick={load} className={`${btn} bg-white text-slate-700 ring-1 ring-slate-200`}>Ver</button>
          </div>
          {r && r.total === 0 && <p className="text-sm text-slate-500">Sin cortesías en el periodo.</p>}
          {r?.byCashier.map((g) => (
            <details key={g.cashierUserId ?? g.cashier} className="rounded-xl border border-slate-200 px-3 py-2">
              <summary className="cursor-pointer text-sm text-slate-800"><strong>{g.cashier}</strong> · {g.count} cortesía{g.count === 1 ? '' : 's'}</summary>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {g.items.map((it) => (
                  <li key={it.id}>{new Date(it.at).toLocaleString('es-MX')} · {it.branch ?? '—'} · {it.description} · miembro {formatLoyaltyCode(it.memberCode)}</li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
